// Design System for AP Automate - Dark Blue Theme
export const theme = {
  // Colors - Dark Blue Theme
  colors: {
    // Primary blues
    primary: {
      50: '#eff6ff',
      100: '#dbeafe',
      200: '#bfdbfe',
      300: '#93c5fd',
      400: '#60a5fa',
      500: '#3b82f6',
      600: '#2563eb',
      700: '#1d4ed8',
      800: '#1e40af',
      900: '#1e3a8a',
      950: '#172554',
    },
    // Background colors
    background: {
      dark: '#0f172a',
      DEFAULT: '#1e293b',
      light: '#334155',
      lighter: '#475569',
      lightest: '#64748b',
    },
    // Text colors
    text: {
      primary: '#f8fafc',
      secondary: '#e2e8f0',
      tertiary: '#94a3b8',
      muted: '#64748b',
    },
    // Status colors
    status: {
      success: '#10b981',
      error: '#ef4444',
      warning: '#f59e0b',
      info: '#3b82f6',
    },
    // Border colors
    border: {
      light: '#334155',
      DEFAULT: '#475569',
      dark: '#1e293b',
    },
  },
  // Typography
  fontFamily: {
    sans: ['Inter', 'system-ui', 'sans-serif'],
    mono: ['Menlo', 'Monaco', 'Courier New', 'monospace'],
  },
  // Shadows
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.1)',
    DEFAULT: '0 4px 6px -1px rgba(0, 0, 0, 0.2)',
    md: '0 10px 15px -3px rgba(0, 0, 0, 0.2)',
    lg: '0 20px 25px -5px rgba(0, 0, 0, 0.2)',
    xl: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.1)',
  },
  // Border radius
  borderRadius: {
    none: '0',
    sm: '0.25rem',
    DEFAULT: '0.5rem',
    md: '0.75rem',
    lg: '1rem',
    full: '9999px',
  },
  // Spacing
  spacing: {
    px: '1px',
    0: '0',
    0.5: '0.125rem',
    1: '0.25rem',
    1.5: '0.375rem',
    2: '0.5rem',
    2.5: '0.625rem',
    3: '0.75rem',
    3.5: '0.875rem',
    4: '1rem',
    5: '1.25rem',
    6: '1.5rem',
    7: '1.75rem',
    8: '2rem',
    9: '2.25rem',
    10: '2.5rem',
    12: '3rem',
    14: '3.5rem',
    16: '4rem',
    20: '5rem',
    24: '6rem',
    28: '7rem',
    32: '8rem',
    36: '9rem',
    40: '10rem',
    44: '11rem',
    48: '12rem',
    52: '13rem',
    56: '14rem',
    60: '15rem',
    64: '16rem',
    72: '18rem',
    80: '20rem',
    96: '24rem',
  },
  // Breakpoints
  screens: {
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
  },
  // Z-index
  zIndex: {
    auto: 'auto',
    0: '0',
    10: '10',
    20: '20',
    30: '30',
    40: '40',
    50: '50',
  },
  // Transitions
  transition: {
    DEFAULT: 'all 0.2s ease-in-out',
    transform: 'transform 0.2s ease-in-out',
    opacity: 'opacity 0.2s ease-in-out',
    colors: 'color, background-color, border-color, text-decoration-color, fill, stroke 0.2s ease-in-out',
  },
};

// Utility function to get color classes
export const getColorClasses = (colorType, shade = 500) => {
  if (theme.colors[colorType]) {
    if (typeof theme.colors[colorType] === 'string') {
      return theme.colors[colorType];
    }
    return theme.colors[colorType][shade] || theme.colors[colorType][500] || theme.colors.primary[500];
  }
  return theme.colors.primary[shade] || theme.colors.primary[500];
};

// Common component styles
export const componentStyles = {
  // Card with subtle shadow and border
  card: {
    base: 'rounded-lg border border-opacity-10 shadow-sm',
    variants: {
      elevated: 'bg-opacity-50 border-opacity-20 shadow-md',
      outlined: 'bg-opacity-10 border border-opacity-20',
      filled: 'bg-opacity-20',
    },
  },
  // Button styles
  button: {
    base: 'inline-flex items-center justify-center rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none transition-colors',
    variants: {
      primary: 'bg-primary-600 text-white hover:bg-primary-700 focus:ring-primary-500',
      secondary: 'bg-secondary-600 text-white hover:bg-secondary-700 focus:ring-secondary-500',
      outline: 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:ring-primary-500',
      ghost: 'hover:bg-gray-100 text-gray-700 focus:ring-primary-500',
      link: 'text-primary-600 hover:text-primary-800 focus:ring-primary-500',
    },
    sizes: {
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-4 py-2 text-sm',
      lg: 'px-6 py-3 text-base',
    },
  },
  // Input styles
  input: {
    base: 'block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm',
    error: 'border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:outline-none focus:ring-red-500',
  },
};

// Combine all theme exports
const themeConfig = {
  ...theme,
  getColorClasses,
  ...componentStyles,
};

// Export everything
export default themeConfig;